import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

export enum PostHogFeatureFlagMap {
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppBuilder = 'enable_app_builder',
  EnableAppBuilderInspector = 'app_builder_inspector',
  EnableAppBuilderStopDuringBuild = 'app-builder-stop-during-build',
  EnableAppBuilderTransfer = 'enable_app_builder_transfer',
  EnableAppBuilderQATab = 'enable_app_builder_qa_tab',
  EnableAppBuilderSettingsNotifications = 'enable_app_builder_settings_notifications',
  EnableBuilderChatHistoryCache = 'enable_builder_chat_history_cache',
  EnableQATwinIsolation = 'qa_twin_isolation',
  EnablePlatformVerifiedUpdates = 'platform-verified-updates',
  EnableAppBuilderNewSession = 'enable-app-builder-new-session',
  EnableAppBuilderProductAgentOpenRouter = 'app-builder-product-agent-openrouter',
  EnableAppBuilderBreadcrumbEditorCaret = 'app-builder-breadcrumb-editor-caret',
  EnableAppBuilderAppOwnedHeader = 'app-builder-app-owned-header',
  EnableAppBuilderHaiku55 = 'app-builder-haiku-5-5',
  EnableAppBuilderSuggestedPrompts = 'enable-app-builder-suggested-prompts',
  EnableAppEntryPoints = 'app-entry-points',
  EnableAppRanking = 'app-ranking',
  EnableAppUpdates = 'enable-app-updates',
  EnableAutoModeSelector = 'auto-mode-selector',
  EnableGptModels = 'gpt-models',
  EnableExtendedModels = 'extended-models',
  EnableGrowthLoops = 'growth-loops',
  EnableBetaApps = 'enable-beta-apps',
  EnableCompanyImport = 'enable_company_import',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageAppNotification = 'message-app-notification',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableNewSidebar = 'new-sidebar',
  EnableNewSettings = 'new-settings',
  EnableNewCustomFields = 'new-custom-fields',
  EnableOnboardingActivation = 'onboarding-activation',
  EnableAuditLogs = 'audit_logs',
  EnableFeatureAppVisibility = 'app_visibility_ocmc_support',
  EnableEventsApiDateFilters = 'events_api_date_filters',
  EnableMcpDashboardUi = 'mcp_dashboard_ui',
  EnableIterateOnProjectGithubActions = 'iterate_on_project_github_actions',
  EnableProductAgent = 'enable_product_agent',
  EnableAssistantRunner = 'enable_assistant_runner',
  EnableMultiInstanceApps = 'enable_multi_instance_apps',
  EnableStudioSidebar = 'studio-sidebar',
  EnableStudioTemplates = 'studio-templates',
  EnableStudioTemplatesAddAppV2 = 'studio-templates-add-app-v2',
  EnableStudioTemplatesPublish = 'studio-templates-publish',
  EnableStudioOnboarding = 'studioOnboarding',
  EnableStudioFTUX = 'studioFTUX',
  EnableStudioPricing = 'studio-pricing',
  EnableChurnAccessLock = 'churn-access-lock',
  EnableMessagingEnterNewLine = 'messaging_enter_new_line',
  EnableChatWebsocketFallback = 'chat-websocket-fallback',
  EnableBrowserTraceIds = 'browser-trace-ids',
  EnableOneTimeCreditPacks = 'one-time-credit-packs',
  EnableWorkspaceDeletion = 'workspace-deletion',
  EnableWidgets = 'widgets-prototype',
  EnableDeepLinkAppInnerLocation = 'deep-link-app-inner-location',
  EnableDeepLinkBridgeNavV2 = 'deep-link-bridge-nav-v2',
  EnableDeepLinkWorkspacePrefix = 'deep-link-workspace-prefix',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableAppBuilderInspector: false,
  EnableAppBuilderStopDuringBuild: false,
  EnableAppBuilderTransfer: false,
  EnableAppBuilderQATab: false,
  EnableAppBuilderSettingsNotifications: false,
  EnableBuilderChatHistoryCache: false,
  EnableQATwinIsolation: false,
  EnablePlatformVerifiedUpdates: false,
  EnableAppBuilderNewSession: false,
  EnableAppBuilderProductAgentOpenRouter: false,
  EnableAppBuilderBreadcrumbEditorCaret: false,
  EnableAppBuilderAppOwnedHeader: false,
  EnableAppBuilderHaiku55: false,
  EnableAppBuilderSuggestedPrompts: false,
  EnableAppEntryPoints: false,
  EnableAppRanking: false,
  EnableAppUpdates: false,
  EnableAutoModeSelector: false,
  EnableGptModels: false,
  EnableExtendedModels: false,
  EnableGrowthLoops: false,
  EnableBetaApps: false,
  EnableCompanyImport: false,
  EnableDuplicateFileName: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableMessageAppNotification: false,
  EnableMicrodepositVerification: false,
  EnableNewSidebar: false,
  EnableNewSettings: false,
  EnableNewCustomFields: false,
  EnableOnboardingActivation: false,
  EnableAuditLogs: false,
  EnableEventsApiDateFilters: false,
  EnableFeatureAppVisibility: false,
  EnableMcpDashboardUi: false,
  EnableIterateOnProjectGithubActions: false,
  EnableProductAgent: false,
  EnableAssistantRunner: false,
  EnableMultiInstanceApps: false,
  EnableStudioSidebar: false,
  EnableStudioTemplates: false,
  EnableStudioTemplatesAddAppV2: false,
  EnableStudioTemplatesPublish: false,
  EnableStudioOnboarding: false,
  EnableStudioFTUX: false,
  EnableStudioPricing: false,
  EnableChurnAccessLock: false,
  EnableMessagingEnterNewLine: false,
  EnableChatWebsocketFallback: false,
  EnableBrowserTraceIds: false,
  EnableOneTimeCreditPacks: false,
  EnableWorkspaceDeletion: false,
  EnableWidgets: false,
  EnableDeepLinkAppInnerLocation: false,
  EnableDeepLinkBridgeNavV2: false,
  EnableDeepLinkWorkspacePrefix: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = never;

const LOCAL_FEATURE_FLAGS_ON_ENV = 'LOCAL_FEATURE_FLAGS_ON';

let warnedIgnoredLocalFlags = false;

/**
 * Forces the flags listed in LOCAL_FEATURE_FLAGS_ON on, for localstack stacks
 * only: PostHog has no record of workspaces created on a throwaway stack, so
 * gated features would otherwise always be off there.
 */
export const applyLocalFeatureFlagOverrides = (
  posthogFlags: Record<string, FeatureFlagValue>,
  isLocalstack: boolean,
  rawOverride: string | undefined = process.env[LOCAL_FEATURE_FLAGS_ON_ENV],
): Record<string, FeatureFlagValue> => {
  const keys = (rawOverride ?? '')
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
  if (keys.length === 0) {
    return posthogFlags;
  }
  if (!isLocalstack) {
    if (!warnedIgnoredLocalFlags) {
      warnedIgnoredLocalFlags = true;
      console.warn(
        `${LOCAL_FEATURE_FLAGS_ON_ENV} is ignored outside a localstack stack`,
      );
    }
    return posthogFlags;
  }
  return { ...posthogFlags, ...Object.fromEntries(keys.map((k) => [k, true])) };
};

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableAppBuilderInspector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderInspector],
  ),
  EnableAppBuilderStopDuringBuild: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild],
  ),
  EnableAppBuilderTransfer: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderTransfer],
  ),
  EnableAppBuilderQATab: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderQATab],
  ),
  EnableAppBuilderSettingsNotifications: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderSettingsNotifications],
  ),
  EnableBuilderChatHistoryCache: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBuilderChatHistoryCache],
  ),
  EnableQATwinIsolation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableQATwinIsolation],
  ),
  EnablePlatformVerifiedUpdates: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnablePlatformVerifiedUpdates],
  ),
  EnableAppBuilderNewSession: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderNewSession],
  ),
  EnableAppBuilderProductAgentOpenRouter: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderProductAgentOpenRouter],
  ),
  EnableAppBuilderBreadcrumbEditorCaret: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderBreadcrumbEditorCaret],
  ),
  EnableAppBuilderAppOwnedHeader: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderAppOwnedHeader],
  ),
  EnableAppBuilderSuggestedPrompts: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderSuggestedPrompts],
  ),
  EnableAppEntryPoints: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppEntryPoints],
  ),
  EnableAppRanking: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppRanking],
  ),
  EnableAppUpdates: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppUpdates],
  ),
  EnableAutoModeSelector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAutoModeSelector],
  ),
  EnableAppBuilderHaiku55: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderHaiku55],
  ),
  EnableGptModels: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableGptModels]),
  EnableExtendedModels: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableExtendedModels],
  ),
  EnableGrowthLoops: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableGrowthLoops],
  ),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageAppNotification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessageAppNotification],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableNewSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewSidebar],
  ),
  EnableNewSettings: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewSettings],
  ),
  EnableNewCustomFields: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewCustomFields],
  ),
  EnableOnboardingActivation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOnboardingActivation],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
  EnableFeatureAppVisibility: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFeatureAppVisibility],
  ),
  EnableEventsApiDateFilters: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableEventsApiDateFilters],
  ),
  EnableMcpDashboardUi: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMcpDashboardUi],
  ),
  EnableIterateOnProjectGithubActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions],
  ),
  EnableProductAgent: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableProductAgent],
  ),
  EnableAssistantRunner: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAssistantRunner],
  ),
  EnableMultiInstanceApps: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMultiInstanceApps],
  ),
  EnableStudioSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioSidebar],
  ),
  EnableStudioTemplates: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplates],
  ),
  EnableStudioTemplatesAddAppV2: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplatesAddAppV2],
  ),
  EnableStudioTemplatesPublish: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplatesPublish],
  ),
  EnableStudioOnboarding: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioOnboarding],
  ),
  EnableStudioFTUX: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioFTUX],
  ),
  EnableStudioPricing: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioPricing],
  ),
  EnableChurnAccessLock: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableChurnAccessLock],
  ),
  EnableMessagingEnterNewLine: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessagingEnterNewLine],
  ),
  EnableChatWebsocketFallback: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableChatWebsocketFallback],
  ),
  EnableBrowserTraceIds: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBrowserTraceIds],
  ),
  EnableOneTimeCreditPacks: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOneTimeCreditPacks],
  ),
  EnableWorkspaceDeletion: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableWorkspaceDeletion],
  ),
  EnableWidgets: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableWidgets]),
  EnableDeepLinkAppInnerLocation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDeepLinkAppInnerLocation],
  ),
  EnableDeepLinkBridgeNavV2: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDeepLinkBridgeNavV2],
  ),
  EnableDeepLinkWorkspacePrefix: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDeepLinkWorkspacePrefix],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableAppBuilderInspector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderInspector,
  ),
  EnableAppBuilderHaiku55: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderHaiku55,
  ),
  EnableAppBuilderStopDuringBuild: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild,
  ),
  EnableAppBuilderTransfer: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderTransfer,
  ),
  EnableAppBuilderQATab: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderQATab,
  ),
  EnableAppBuilderSettingsNotifications: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderSettingsNotifications,
  ),
  EnableBuilderChatHistoryCache: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBuilderChatHistoryCache,
  ),
  EnableQATwinIsolation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableQATwinIsolation,
  ),
  EnablePlatformVerifiedUpdates: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnablePlatformVerifiedUpdates,
  ),
  EnableAppBuilderNewSession: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderNewSession,
  ),
  EnableAppBuilderProductAgentOpenRouter: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderProductAgentOpenRouter,
  ),
  EnableAppBuilderBreadcrumbEditorCaret: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderBreadcrumbEditorCaret,
  ),
  EnableAppBuilderAppOwnedHeader: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderAppOwnedHeader,
  ),
  EnableAppBuilderSuggestedPrompts: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderSuggestedPrompts,
  ),
  EnableAppEntryPoints: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppEntryPoints,
  ),
  EnableAppRanking: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppRanking,
  ),
  EnableAppUpdates: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppUpdates,
  ),
  EnableAutoModeSelector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAutoModeSelector,
  ),
  EnableGptModels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableGptModels,
  ),
  EnableExtendedModels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableExtendedModels,
  ),
  EnableGrowthLoops: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableGrowthLoops,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageAppNotification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageAppNotification,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableNewSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewSidebar,
  ),
  EnableNewSettings: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewSettings,
  ),
  EnableNewCustomFields: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewCustomFields,
  ),
  EnableOnboardingActivation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOnboardingActivation,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableEventsApiDateFilters: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableEventsApiDateFilters,
  ),
  EnableFeatureAppVisibility: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFeatureAppVisibility,
  ),
  EnableMcpDashboardUi: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMcpDashboardUi,
  ),
  EnableIterateOnProjectGithubActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions,
  ),
  EnableProductAgent: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableProductAgent,
  ),
  EnableAssistantRunner: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAssistantRunner,
  ),
  EnableMultiInstanceApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMultiInstanceApps,
  ),
  EnableStudioSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioSidebar,
  ),
  EnableStudioTemplates: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplates,
  ),
  EnableStudioTemplatesAddAppV2: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplatesAddAppV2,
  ),
  EnableStudioTemplatesPublish: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplatesPublish,
  ),
  EnableStudioOnboarding: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioOnboarding,
  ),
  EnableStudioFTUX: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioFTUX,
  ),
  EnableStudioPricing: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioPricing,
  ),
  EnableChurnAccessLock: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableChurnAccessLock,
  ),
  EnableMessagingEnterNewLine: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessagingEnterNewLine,
  ),
  EnableChatWebsocketFallback: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableChatWebsocketFallback,
  ),
  EnableBrowserTraceIds: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBrowserTraceIds,
  ),
  EnableOneTimeCreditPacks: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOneTimeCreditPacks,
  ),
  EnableWorkspaceDeletion: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableWorkspaceDeletion,
  ),
  EnableWidgets: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableWidgets,
  ),
  EnableDeepLinkAppInnerLocation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDeepLinkAppInnerLocation,
  ),
  EnableDeepLinkBridgeNavV2: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDeepLinkBridgeNavV2,
  ),
  EnableDeepLinkWorkspacePrefix: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDeepLinkWorkspacePrefix,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
